import React, { useState } from "react";
import Modal from "../Modal/Modal";
import classNames from "classnames";
import { useAppDispatch, useAppSelector } from "../../Redux/app/hooks";
import Loading from "../Loading/Loading";
import { useSelector } from "react-redux";
import Select from "react-select";
import { DropDownStylesBroker, formatNumber } from "../../helpers/helper";
import Button from "../Button/Button";
import { update_form_data } from "../../Redux/features/OrderSection/OrderSectionSlice";
import { toast } from "react-toastify";

type Props = {
	market_id: string;
	security_set: any;
	conValue: any;
};

const AlgoForm = ({ market_id, security_set, conValue }: Props) => {
	const dispatch = useAppDispatch();
 	const loader = useAppSelector((state) => state.dashBoard.fut_opt_loader);
	const [isAlgoModalOpen, setIsAlgoModalOpen] = useState(false);
	const form = useAppSelector((state) => state.orderSection.form_data);
	const isOptionOrder = (conValue?.ContractId)?true:false;
	const [algoFormData, setAlgoFormData] = useState({
		algoType: "Adaptive",
		algoTypeName: "Adaptive Algo",
		maxPrice: 0,
		riskAversion: "GetDone",
		startTime: null,
		endTime: null,
		allowPastEndTime: false,
		forceCompletion: false,
		liquidity: false,
		targetPercentage: 0,
		targetPercentageChangeRate: 0,
		terminalTargetPercentage: 0,
		initialTargetPercentage: 0,
		maximumTargetPercentage: 0,
		minimumTargetPercentage: 0,
		tradeStrategy: "Marketable",
		priority: "Urgent",
		displaySize: 0,
		quantityOfIncrement: null,
		timeInterval: 0,
		associatedNumber: null,
		randomiseSize: false,
		randomiseTime: false,
		speedUp: false,
		waitForCurrentOrder: false,
		catchUpInTime: false,
	});
// Add validation state
const [validationErrors, setValidationErrors] = useState<string[]>([]);

// Validation function
const validateAlgoForm = (): boolean => {
	const errors: string[] = [];

	if (!algoFormData.algoType) {
		errors.push("Please select an Algorithm");
	}

	if (algoFormData.algoType === "Adaptive" && !algoFormData.priority) {
		errors.push("Priority is required for Adaptive Algo");
	}
	if (["ArrivalPx", "ClosePx", "Vwap", "BalanceImpactRisk", "MinImpact"].includes(algoFormData.algoType || "") && !algoFormData.maxPrice) {
		errors.push("Max Percentage is required");
	}
	if (["PctVol", "PctVolPx"].includes(algoFormData.algoType || "") && !algoFormData.targetPercentage) {
		errors.push("Target Percentage is required");
	}
	if (["PctVolPx"].includes(algoFormData.algoType || "") && !algoFormData.targetPercentageChangeRate) {
		errors.push("Target Percentage change rate is required");
	}
	if (["PctVolPx"].includes(algoFormData.algoType || "") && !algoFormData.maximumTargetPercentage) {
		errors.push("Maximum Target Percentage is required");
	}
	if (["PctVolPx"].includes(algoFormData.algoType || "") && !algoFormData.minimumTargetPercentage) {
		errors.push("Minimum Target Percentage is required");
	}

	const timeRequiredAlgos = [
		"ArrivalPx", "ClosePx", "DarkIce", "PctVol", 
		"Twap", "PctVolPx", "AD", "PctVolSz", 
		"PctVolTm", "Vwap"
	];
	if (timeRequiredAlgos.includes(algoFormData.algoType || "") && !algoFormData.startTime) {
		errors.push("Start Time is required");
	}

	if (timeRequiredAlgos.includes(algoFormData.algoType || "") && algoFormData.startTime && !algoFormData.endTime) {
		errors.push("End Time is required");
	}else{
		if (algoFormData.startTime && algoFormData.endTime) {
			const startTime = new Date(`1970-01-01T${algoFormData.startTime}:00`);
			const endTime = new Date(`1970-01-01T${algoFormData.endTime}:00`);
			if (endTime <= startTime) {
				errors.push("End Time must be greater than Start Time");
			}
		}
	}
	const riskAversionAlgo = ["ArrivalPx", "ClosePx","BalanceImpactRisk"];
	if (riskAversionAlgo.includes(algoFormData.algoType || "") && !algoFormData.riskAversion) {
		errors.push("Risk Aversion is required");
	}
	const tradeStrategyAlgo = ["Twap"];
	if (tradeStrategyAlgo.includes(algoFormData.algoType || "") && !algoFormData.tradeStrategy) {
		errors.push("Trade Strategy is required");
	}

	const displaySizeAlgos = ["DarkIce"];
	if (displaySizeAlgos.includes(algoFormData.algoType || "") && (!algoFormData.displaySize || algoFormData.displaySize == 0)) {
		errors.push("Display size is required");
	}
	const adAlgos = ["AD"];
	if (adAlgos.includes(algoFormData.algoType || "") && !algoFormData.quantityOfIncrement) {
		errors.push("Quantity of increment is required");
	}
	if (adAlgos.includes(algoFormData.algoType || "") && !algoFormData.timeInterval) {
		errors.push("Time interval is required");
	}
	if (adAlgos.includes(algoFormData.algoType || "") && !algoFormData.associatedNumber) {
		errors.push("Associated number is required");
	}

	setValidationErrors(errors);
	return errors.length === 0;
};

const handleAlgoData = (e: any, type: number) => {
	// Clear validation errors when user makes changes
	setValidationErrors([]);
	
	if(type === 1){
		setAlgoFormData(prev => ({
			...prev,
			algoType: e?.value || null,
			algoTypeName: e?.label || null
		}));
	}
	else if(type === 2){
		setAlgoFormData(prev => ({
			...prev,
			priority: e?.value || null,
		}));
	}
	else if(type === 3){
		let value = e?.target?.value || e?.value || null;
		if(value > 50){
			value = 50;
		}
		let val_format = formatNumber(String(value),5);
		if (val_format !== false) {
			value = val_format;
		}
		setAlgoFormData(prev => ({
			...prev,
			maxPrice: value
		}));
	}
	else if(type === 4){
		setAlgoFormData(prev => ({
			...prev,
			riskAversion: e?.value || null,
		}));
	}
	else if(type === 5){
		setAlgoFormData(prev => ({
			...prev,
			startTime: e?.target?.value || e?.value || null,
		}));
	}
	else if(type === 6){
		setAlgoFormData(prev => ({
			...prev,
			endTime: e?.target?.value || e?.value || null,
		}));
	}
	else if(type === 7){
		setAlgoFormData(prev => ({
			...prev,
			allowPastEndTime: e?.target?.checked || false,
		}));
	}
	else if(type === 8){
		setAlgoFormData(prev => ({
			...prev,
			forceCompletion: e?.target?.checked || false,
		}));
	}
	else if(type === 9){
		setAlgoFormData(prev => ({
			...prev,
			displaySize: e?.target?.value || e?.value || null
		}));
	}
	else if(type === 10){
		setAlgoFormData(prev => ({
			...prev,
			liquidity: e?.target?.checked || false,
		}));
	}
	else if(type === 11){
		let value = e?.target?.value || e?.value || 0;
		if(value > 50){
			value = 50;
		}
		let val_format = formatNumber(String(value),5);
		if (val_format !== false) {
			value = val_format;
		}
		setAlgoFormData(prev => ({
			...prev,
			targetPercentage: value
		}));
	}
	else if(type === 12){
		setAlgoFormData(prev => ({
			...prev,
			tradeStrategy: e?.value || null,
		}));
	}
	else if(type === 13){
		let value = e?.target?.value || e?.value || 0;
		if(value > 50){
			value = 50;
		}
		let val_format = formatNumber(String(value),5);
		if (val_format !== false) {
			value = val_format;
		}
		setAlgoFormData(prev => ({
			...prev,
			targetPercentageChangeRate: value
		}));
	}
	else if(type === 14){
		let value = e?.target?.value || e?.value || 0;
		if(value > 50){
			value = 50;
		}
		let val_format = formatNumber(String(value),5);
		if (val_format !== false) {
			value = val_format;
		}
		setAlgoFormData(prev => ({
			...prev,
			maximumTargetPercentage: value
		}));
	}
	else if(type === 15){
		let value = e?.target?.value || e?.value || 0;
		if(value > 50){
			value = 50;
		}
		let val_format = formatNumber(String(value),5);
		if (val_format !== false) {
			value = val_format;
		}
		setAlgoFormData(prev => ({
			...prev,
			minimumTargetPercentage: value
		}));
	}
	else if(type === 16){
		let value = e?.target?.value || e?.value || 0;
		if(value > 50){
			value = 50;
		}
		let val_format = formatNumber(String(value),5);
		if (val_format !== false) {
			value = val_format;
		}
		setAlgoFormData(prev => ({
			...prev,
			terminalTargetPercentage: value
		}));
	}
	else if(type === 17){
		let value = e?.target?.value || e?.value || 0;
		if(value > 50){
			value = 50;
		}
		let val_format = formatNumber(String(value),5);
		if (val_format !== false) {
			value = val_format;
		}
		setAlgoFormData(prev => ({
			...prev,
			initialTargetPercentage: value
		}));
	}
	else if(type === 18){
		setAlgoFormData(prev => ({
			...prev,
			speedUp: e?.target?.checked || false,
		}));
	}
	else if(type === 19){
		setAlgoFormData(prev => ({
			...prev,
			quantityOfIncrement: e?.target?.value || null,
		}));
	}
	else if(type === 20){
		setAlgoFormData(prev => ({
			...prev,
			timeInterval: e?.target?.value || null,
		}));
	}
	else if(type === 22){
		setAlgoFormData(prev => ({
			...prev,
			catchUpInTime: e?.target?.checked || false,
		}));
	}
	else if(type === 23){
		setAlgoFormData(prev => ({
			...prev,
			waitForCurrentOrder: e?.target?.checked || false,
		}));
	}
	else if(type === 24){
		setAlgoFormData(prev => ({
			...prev,
			randomiseSize: e?.target?.checked || false,
		}));
	}
	else if(type === 25){
		setAlgoFormData(prev => ({
			...prev,
			randomiseTime: e?.target?.checked || false,
		}));
	}
	else if(type === 26){
		setAlgoFormData(prev => ({
			...prev,
			associatedNumber: e?.target?.value || null,
		}));
	}
};

const resetAlgoFormData = () => {
	setAlgoFormData({
		algoType: (isOptionOrder ? "BalanceImpactRisk" : "Adaptive"),
		algoTypeName: (isOptionOrder ? "Balance Impact Risk" : "Adaptive Algo"),
		maxPrice: 0,
		riskAversion: "GetDone",
		startTime: null,
		endTime: null,
		allowPastEndTime: false,
		forceCompletion: false,
		liquidity: false,
		targetPercentage: 0,
		targetPercentageChangeRate: 0,
		initialTargetPercentage: 0,
		terminalTargetPercentage: 0,
		minimumTargetPercentage: 0,
		maximumTargetPercentage: 0,
		tradeStrategy: "Marketable",
		priority: "Urgent",
		displaySize: 0,
		quantityOfIncrement: null,
		timeInterval: 0,
		associatedNumber: null,
		randomiseSize: false,
		randomiseTime: false,
		speedUp: false,
		waitForCurrentOrder: false,
		catchUpInTime: false,
	});
	setValidationErrors([]); // Clear validation errors

	let oldFormData = form?.algo;
	 if(oldFormData){
		setAlgoFormData({
			algoType: oldFormData.algoType,
			algoTypeName: oldFormData.algoTypeName,
			maxPrice: oldFormData.maxPrice,
			riskAversion: oldFormData.riskAversion,
			startTime: oldFormData.startTime,
			endTime: oldFormData.endTime,
			allowPastEndTime: oldFormData.allowPastEndTime,
			forceCompletion:oldFormData.forceCompletion,
			liquidity:oldFormData.liquidity,
			targetPercentage:oldFormData.targetPercentage,
			targetPercentageChangeRate:oldFormData.targetPercentageChangeRate,
			initialTargetPercentage:oldFormData.initialTargetPercentage,
			terminalTargetPercentage:oldFormData.terminalTargetPercentage,
			minimumTargetPercentage:oldFormData.minimumTargetPercentage,
			maximumTargetPercentage:oldFormData.maximumTargetPercentage,
			tradeStrategy:oldFormData.tradeStrategy,
			priority:oldFormData.priority,
			displaySize:oldFormData.displaySize,
			quantityOfIncrement:oldFormData.quantityOfIncrement,
			timeInterval:oldFormData.timeInterval,
			associatedNumber:oldFormData.associatedNumber,
			randomiseSize:oldFormData.randomiseSize,
			randomiseTime:oldFormData.randomiseTime,
			speedUp:oldFormData.speedUp,
			waitForCurrentOrder:oldFormData.waitForCurrentOrder,
			catchUpInTime:oldFormData.catchUpInTime,
		});
	 }
	dispatch(update_form_data({ future: undefined, option: undefined, security_type: undefined }));
};

const handleSaveAlgo = () => {
	// Validate form before saving
	if (!validateAlgoForm()) {
		return;
	}

	dispatch(update_form_data({ future: undefined, option: undefined, security_type: undefined, algo: algoFormData }));
	setIsAlgoModalOpen(false);
	setValidationErrors([]); // Clear errors on successful save
}
  	return (
	  <>
		  {Number(market_id) === 1 && (
			  <div className="flex cursor-pointer bg-[#f0f1f3] items-center m-auto shadow px-[4px] py-[2px] rounded-md text-[#6e6e6e] gap-2">
			<div
			  onClick={() => {
				if (form.day === "good_till_cancel") {
					toast.error("GTC orders are not supported for Algorithm Trading", {
						position: "top-right",
						autoClose: 3000,
						hideProgressBar: false,
						closeOnClick: true,
						pauseOnHover: true,
						draggable: true,
						progress: undefined,
						theme: "colored",
					});
					return; // Prevent opening the algo form
				}
				resetAlgoFormData();
				setIsAlgoModalOpen(true);
				}}
			  className={classNames("px-[4px] text-[10px] py-[2px] rounded", {
				"bg-white": isAlgoModalOpen || form?.algo?.algoType,
			  })}
			>
			  Algo
			</div>
			</div>
		  )}
	  
		<Modal
		  text={`IB Algorithms`}
		  isOpen={isAlgoModalOpen}
		  onClose={() => {
			setIsAlgoModalOpen(false);
		  }}
		>
			<div className="order-form-group p-2 flex flex-col gap-2 w-[480px] h-auto mx-auto" style={{ height: (algoFormData.algoType === "MidPrice") ? "150px" : "auto" }}>
				<div className="order-form-body w-full" style={{ padding: "0" }}>
					<div className="order-form-select-input w-full custome-select select-disabled">
						<label className="select-lable">Algorithm</label>
						<Select
							options={[
								...(Number(market_id) === 1 && !isOptionOrder ? [
									{ value: "Adaptive", label: "Adaptive" },
									{ value: "ArrivalPx", label: "Arrival Price" },
									{ value: "ClosePx", label: "Close Price" },
								] : []),
								...(Number(market_id) === 1 && !isOptionOrder && Number(security_set?.country_id) === 14 ? [{ value: "MidPrice", label: "Mid Price" }] : []),
								...(Number(market_id) === 1 && !isOptionOrder ? [
									{ value: "DarkIce", label: "Dark Ice" },
									{ value: "AD", label: "Accumulate/Distribute" },
									{ value: "PctVol", label: "Percentage of Volume" },
									{ value: "Twap", label: "TWAP" },
									{ value: "PctVolPx", label: "Price Variant Percentage of Volume Strategy" },
									{ value: "PctVolSz", label: "Size Variant Percentage of Volume Strategy" },
									{ value: "PctVolTm", label: "Time Variant Percentage of Volume Strategy" },
									{ value: "Vwap", label: "VWAP" },
								] : []),
								...(Number(market_id) === 1 && isOptionOrder ? [
									{ value: "BalanceImpactRisk", label: "Balance Impact Risk" },
									{ value: "MinImpact", label: "Minimise Impact" }
								] : []),
							].filter(Boolean) as any}
							value={{ value: algoFormData.algoType, label: algoFormData.algoTypeName }}
							onChange={(e) => {
								handleAlgoData(e, 1);
							}}
							styles={{
								...DropDownStylesBroker,
								indicatorSeparator: (provided: any) => ({
									...provided,
									display: "none",
								}),
								dropdownIndicator: (base: any) => ({
									...base,
									color: "inherit",
									padding: "0",
									minHeight: "auto",
									maxHeight: "auto",
									"&:hover": {
									color: "inherit",
									},
								}),
								indicatorsContainer: (provided: any) => ({
									...provided,
								}),
								menuList: (base: any) => ({
									...base,
									padding: 0,
									backgroundColor: "var(--color-white)",
									boxShadow: "0px 3px 6px rgba(var(--color-black-rgb), 0.25) !important",
									border: "none",
									color: "var(--primaryText)",
									borderRadius: 0,
									cursor: "pointer",
									maxHeight: "120px",
									overflowY: "auto",
								}),
							}}
						/>
					</div>
				</div>
				{algoFormData.algoType === "Adaptive" && (
				<div className="flex gap-4 w-full">
					<div className="order-form-select-input w-full custome-select">
						<label className="select-lable">Priority</label>
						<Select
							options={[
								{ value: "Urgent", label: "Urgent" },
								{ value: "Normal", label: "Normal" },
								{ value: "Patient", label: "Patient" }
							] as any}
							value={{ value: algoFormData.priority, label: algoFormData.priority }}
							onChange={(e) => { 
								handleAlgoData(e, 2);
							}}
							styles={{
								...DropDownStylesBroker,
								indicatorSeparator: (provided: any) => ({
									...provided,
									display: "none",
								}),
								dropdownIndicator: (base: any) => ({
									...base,
									color: "inherit",
									padding: "0",
									minHeight: "auto",
									maxHeight: "auto",
									"&:hover": {
									color: "inherit",
									},
								}),
								indicatorsContainer: (provided: any) => ({
									...provided,
								}),
								menuList: (base: any) => ({
									...base,
									padding: 0,
									backgroundColor: "var(--color-white)",
									boxShadow: "0px 3px 6px rgba(var(--color-black-rgb), 0.25) !important",
									border: "none",
									color: "var(--primaryText)",
									borderRadius: 0,
									cursor: "pointer",
									maxHeight: "80px",
									overflowY: "auto",
								}),
							}}
						/>
					</div>
				</div>
				)}

				{ (algoFormData.algoType === "ArrivalPx" || algoFormData.algoType === "ClosePx" || 
				algoFormData.algoType === "Vwap" || algoFormData.algoType === "BalanceImpactRisk" || algoFormData.algoType === "MinImpact") && (
					<div className="flex gap-4 w-full">
						<div className="order-form-body w-full !p-0">
							<div className="order-form-main w-full">
								<div className="input-container w-full">
									<input
										type="number"
										value={algoFormData.maxPrice }
										onChange={(e) => {
											setValidationErrors([]);
											handleAlgoData(e, 3);
										}}
										min="0.01"
										max="50"
										className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
									/>
									<label className="left-2 filled">Max Percentage (0.01 to 50.0)</label>
								</div>
							</div>
						</div>
					</div>
				)}
				{(algoFormData.algoType === "ArrivalPx" || algoFormData.algoType === "ClosePx" || algoFormData.algoType === "BalanceImpactRisk") && (
				<div className="flex gap-4 w-full">
					<div className="order-form-select-input w-full custome-select select-disabled">
					<label className="select-lable">Urgency/Risk Aversion</label>
					<Select
						options={[
							{ value: "GetDone", label: "GetDone" },
							{ value: "Aggressive", label: "Aggressive" },
							{ value: "Neutral", label: "Neutral" },
							{ value: "Passive", label: "Passive" }
						] as any}
						value={{ value: algoFormData.riskAversion, label: algoFormData.riskAversion }}
						onChange={(e) => { 
							handleAlgoData(e, 4);
						}}
						styles={{
							...DropDownStylesBroker,
							indicatorSeparator: (provided: any) => ({
								...provided,
								display: "none",
							}),
							dropdownIndicator: (base: any) => ({
								...base,
								color: "inherit",
								padding: "0",
								minHeight: "auto",
								maxHeight: "auto",
								"&:hover": {
								color: "inherit",
								},
							}),
							indicatorsContainer: (provided: any) => ({
								...provided,
							}),
							menuList: (base: any) => ({
								...base,
								padding: 0,
								backgroundColor: "var(--color-white)",
								boxShadow: "0px 3px 6px rgba(var(--color-black-rgb), 0.25) !important",
								border: "none",
								color: "var(--primaryText)",
								borderRadius: 0,
								cursor: "pointer",
								maxHeight: "80px",
								overflowY: "auto",
							}),
						}}
					/>
					</div>
				</div>
				)}
				{(algoFormData.algoType === "DarkIce") && (
				<div className="flex gap-4 w-full">
					<div className="order-form-body w-full !p-0">
						<div className="order-form-main w-full">
							<div className="input-container w-full">
								<input
									type="number"
									value={algoFormData.displaySize}
									onChange={(e) => { 
										const value = e.target.value;
										// Allow empty value or valid integers
										if (value === '' || /^\d+$/.test(value)) {
											// Remove leading zeros
											const cleanValue = value.replace(/^0+/, '') || '0';
											
											// Create event with cleaned value
											const cleanEvent = {
												...e,
												target: {
													...e.target,
													value: cleanValue
												}
											};
											
											handleAlgoData(cleanEvent, 9);
										} else {
											// If invalid value is pasted, clean it and update
											const cleanValue = value.replace(/\D/g, '').replace(/^0+/, '') || '0';
											const cleanEvent = {
												...e,
												target: {
													...e.target,
													value: cleanValue
												}
											};
											handleAlgoData(cleanEvent, 9);
										}
									}}
									min="1"
									max="50"
									step="1"
									className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
								/>
								<label className="left-2 filled">Display Size</label>
							</div>
						</div>
					</div>
				</div>
				)}
				{(algoFormData.algoType === "PctVol" || algoFormData.algoType === "PctVolPx") && (
				<div className="flex gap-4 w-full">
					<div className="order-form-body w-full !p-0">
						<div className="order-form-main w-full">
							<div className="input-container w-full">
								<input
									type="number"
									value={algoFormData.targetPercentage}
									onChange={(e) => {
										setValidationErrors([]);
										handleAlgoData(e, 11);
									}}
									min="0.01"
									max="50"
									className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
								/>
								<label className="left-2 filled">Target Percentage (0.01 to 50)</label>
							</div>
						</div>
					</div>
				</div>
				)}
				{(algoFormData.algoType === "AD") && (
				<div>
					<div className="flex gap-4 w-full" style={{ marginBottom: "8px" }}>
						<div className="order-form-body w-full !p-0">
							<div className="order-form-main w-full">
								<div className="input-container w-full">
									<input
										type="number"
										value={algoFormData.quantityOfIncrement || ""}
										onChange={(e) => {
											setValidationErrors([]);
											handleAlgoData(e, 19);
										}}
										className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
									/>
									<label className="left-2 filled">Quantity Of Increment</label>
								</div>
							</div>
						</div>
					</div>
					<div className="flex gap-4 w-full" style={{ marginBottom: "8px" }}>
						<div className="order-form-body w-full !p-0">
							<div className="order-form-main w-full">
								<div className="input-container w-full">
									<input
										type="number"
										value={algoFormData.timeInterval}
										onChange={(e) => {
											setValidationErrors([]);
											handleAlgoData(e, 20);
										}}
										className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
									/>
									<label className="left-2 filled">Time interval in seconds between each order</label>
								</div>
							</div>
						</div>
					</div>
					<div className="flex w-full items-center" style={{ marginBottom: "8px" }}>
						<input
							type="checkbox"
							checked={algoFormData.randomiseTime}
							onChange={(e) => handleAlgoData(e,25)}
							className="form-checkbox h-4 w-4 text-blue-600 transition duration-150 ease-in-out rounded"
							style={{ borderRadius: "!25%" }}
						/>
						<label className="select-lable ml-2 cursor-pointer" style={{ color: "black", fontSize: "14px" }}>Randomise time period by +/- 20%</label>
					</div>
					<div className="flex w-full items-center" style={{ marginBottom: "8px" }}>
						<input
							type="checkbox"
							checked={algoFormData.randomiseSize}
							onChange={(e) => handleAlgoData(e,24)}
							className="form-checkbox h-4 w-4 text-blue-600 transition duration-150 ease-in-out rounded"
							style={{ borderRadius: "!25%" }}
						/>
						<label className="select-lable ml-2 cursor-pointer" style={{ color: "black", fontSize: "14px" }}>Randomise size by +/- 55%</label>
					</div>
					<div className="flex gap-4 w-full" style={{ marginBottom: "8px" }}>
						<div className="order-form-body w-full !p-0">
							<div className="order-form-main w-full">
								<div className="input-container w-full">
									<input
										type="number"
										value={algoFormData.associatedNumber || ""}
										onChange={(e) => {
											setValidationErrors([]);
											handleAlgoData(e, 26);
										}}
										className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
									/>
									<label className="left-2 filled">Number associated with the clearing</label>
								</div>
							</div>
						</div>
					</div>
					<div className="flex w-full items-center" style={{ marginBottom: "8px" }}>
						<input
							type="checkbox"
							checked={algoFormData.catchUpInTime}
							onChange={(e) => handleAlgoData(e,22)}
							className="form-checkbox h-4 w-4 text-blue-600 transition duration-150 ease-in-out rounded"
							style={{ borderRadius: "!25%" }}
						/>
						<label className="select-lable ml-2 cursor-pointer" style={{ color: "black", fontSize: "14px" }}>Catch up in time</label>
					</div>
					<div className="flex w-full items-center" style={{ marginBottom: "8px" }}>
						<input
							type="checkbox"
							checked={algoFormData.waitForCurrentOrder}
							onChange={(e) => handleAlgoData(e,23)}
							className="form-checkbox h-4 w-4 text-blue-600 transition duration-150 ease-in-out rounded"
							style={{ borderRadius: "!25%" }}
						/>
						<label className="select-lable ml-2 cursor-pointer" style={{ color: "black", fontSize: "14px" }}>Wait for current order to fill before submitting next order</label>
					</div>
				</div>
				)}
				{(algoFormData.algoType === "Twap") && (
				<div className="flex gap-4 w-full">
					<div className="order-form-select-input w-full custome-select">
					<label className="select-lable">Trade Strategy</label>
					<Select
						options={[
							{ value: "Marketable", label: "Marketable" },
							{ value: "Matching", label: "Matching" },
							{ value: "Midpoint", label: "Midpoint" },
							{ value: "Matching Same Side", label: "Matching Same Side" },
							{ value: "Matching Last", label: "Matching Last" }
							] as any}
						value={{ value: algoFormData.tradeStrategy, label: algoFormData.tradeStrategy }}
						onChange={(e) => { 
							handleAlgoData(e, 12);
						}}
						styles={{
							...DropDownStylesBroker,
							indicatorSeparator: (provided: any) => ({
								...provided,
								display: "none",
							}),
							dropdownIndicator: (base: any) => ({
								...base,
								color: "inherit",
								padding: "0",
								minHeight: "auto",
								maxHeight: "auto",
								"&:hover": {
								color: "inherit",
								},
							}),
							indicatorsContainer: (provided: any) => ({
								...provided,
							}),
							menuList: (base: any) => ({
								...base,
								padding: 0,
								backgroundColor: "var(--color-white)",
								boxShadow: "0px 3px 6px rgba(var(--color-black-rgb), 0.25) !important",
								border: "none",
								color: "var(--primaryText)",
								borderRadius: 0,
								cursor: "pointer",
								maxHeight: "80px",
								overflowY: "auto",
							}),
						}}
					/>
					</div>
				</div>
				)}
				{(algoFormData.algoType === "ArrivalPx" || algoFormData.algoType === "ClosePx" || 
				algoFormData.algoType === "DarkIce" || algoFormData.algoType === "PctVol" || 
				algoFormData.algoType === "Twap" || algoFormData.algoType === "PctVolPx" || algoFormData.algoType === "AD"
				|| algoFormData.algoType === "PctVolSz" || algoFormData.algoType === "PctVolTm" || algoFormData.algoType === "Vwap") && (
				<div className="flex gap-4 w-full">
					<div className="order-form-body !p-0 w-1/2">
						<div className="order-form-main w-full">
							<div className="input-container w-full custom-algo-time">
								<input
									type="time"
									value={algoFormData.startTime || ""}
									onChange={(e) => {
										setValidationErrors([]); // Clear validation errors
										handleAlgoData(e, 5);
									}}
									className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
								/>
								<label className="left-2 filled">Start Time 
									{Number(security_set?.country_id) === 14 ? (
										<span> (US/Eastern)</span>
									) : (
										<span> (UTC)</span>
									)}
								</label>
							</div>
						</div>
					</div>
					<div className="order-form-body !p-0 w-1/2">
						<div className="order-form-main w-full">
							<div className="input-container w-full custom-algo-time">
								<input
									type="time"
									min={algoFormData.startTime || new Date().toISOString().slice(0, 16)}
									max={new Date(Date.now() + 10 * 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16)}
									value={algoFormData.endTime || ""}
									onChange={(e) => {
										setValidationErrors([]);
										handleAlgoData(e, 6);
									}}
									className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
								/>
								<label className="left-2 filled">End Time
									{Number(security_set?.country_id) === 14 ? (
										<span> (US/Eastern)</span>
									) : (
										<span> (UTC)</span>
									)}
								</label>
							</div>
						</div>
					</div>
				</div>
				)}
				{(algoFormData.algoType === "PctVolSz" || algoFormData.algoType === "PctVolTm") && (
				<div className="flex gap-4 w-full" style={{ marginBottom: "8px" }}>
					<div className="order-form-body w-full !p-0">
						<div className="order-form-main w-full">
							<div className="input-container w-full">
								<input
									type="number"
									value={algoFormData.initialTargetPercentage}
									onChange={(e) => {
										setValidationErrors([]);
										handleAlgoData(e, 17);
									}}
									min="0.01"
									max="50"
									className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
								/>
								<label className="left-2 filled">Initial Target Percentage (0.01 to 50)</label>
							</div>
						</div>
					</div>
					<div className="order-form-body w-full !p-0">
						<div className="order-form-main w-full">
							<div className="input-container w-full">
								<input
									type="number"
									value={algoFormData.terminalTargetPercentage}
									onChange={(e) => {
										setValidationErrors([]);
										handleAlgoData(e, 16);
									}}
									min="0.01"
									max="50"
									className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
								/>
								<label className="left-2 filled">Terminal Target Percentage	(0.01 to 50)</label>
							</div>
						</div>
					</div>
				</div>
				)}
				{(algoFormData.algoType === "ArrivalPx" || algoFormData.algoType === "DarkIce" || algoFormData.algoType === "Twap" || algoFormData.algoType === "Vwap") && (
				<div className="flex w-full items-center">
 					<input
 						type="checkbox"
 						checked={algoFormData.allowPastEndTime}
 						onChange={(e) => handleAlgoData(e,7)}
 						className="form-checkbox h-4 w-4 text-blue-600 transition duration-150 ease-in-out rounded"
 					/>
					<label className="select-lable ml-2 cursor-pointer" style={{ color: "black", fontSize: "14px" }}>Allow trading past end time</label>
 				</div>
				)}
				{(algoFormData.algoType === "ArrivalPx" || algoFormData.algoType === "ClosePx" || algoFormData.algoType === "BalanceImpactRisk") && (
				<div className="flex w-full items-center">
 					<input
 						type="checkbox"
 						checked={algoFormData.forceCompletion}
 						onChange={(e) => handleAlgoData(e,8)}
 						className="form-checkbox h-4 w-4 text-blue-600 transition duration-150 ease-in-out rounded"
 					/>
					<label className="select-lable ml-2 cursor-pointer" style={{ color: "black", fontSize: "14px" }}>Attempt completion by EOD</label>
 				</div>
				)}
				{(algoFormData.algoType === "PctVolPx") && (
				<div className="flex gap-4 w-full">
					<div className="order-form-body w-full !p-0">
						<div className="order-form-main w-full">
							<div className="input-container w-full">
								<input
									type="number"
									value={algoFormData.targetPercentageChangeRate}
									onChange={(e) => {
										setValidationErrors([]);
										handleAlgoData(e, 13);
									}}
									min="0.01"
									max="50"
									className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
								/>
								<label className="left-2 filled">Target Percentage Change Rate (0.01 to 50)</label>
							</div>
						</div>
					</div>
				</div>
				)}
				{(algoFormData.algoType === "PctVolPx") && (
				<div className="flex gap-4 w-full">
					<div className="order-form-body w-full !p-0">
						<div className="order-form-main w-full">
							<div className="input-container w-full">
								<input
									type="number"
									value={algoFormData.minimumTargetPercentage}
									onChange={(e) => {
										setValidationErrors([]);
										handleAlgoData(e, 15);
									}}
									min="0.01"
									max="50"
									className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
								/>
								<label className="left-2 filled">Minimum Target Percentage (0.01 to 50)</label>
							</div>
						</div>
					</div>
					<div className="order-form-body w-full !p-0">
						<div className="order-form-main w-full">
							<div className="input-container w-full">
								<input
									type="number"
									value={algoFormData.maximumTargetPercentage}
									onChange={(e) => {
										setValidationErrors([]);
										handleAlgoData(e, 14);
									}}
									min="0.01"
									max="50"
									className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
								/>
								<label className="left-2 filled">Maximum Target Percentage (0.01 to 50)</label>
							</div>
						</div>
					</div>
				</div>
				)}
				{(algoFormData.algoType === "PctVol" || algoFormData.algoType === "PctVolPx" || 
				algoFormData.algoType === "PctVolSz" || algoFormData.algoType === "PctVolTm" || algoFormData.algoType === "Vwap") && (
				<div className="flex w-full items-center">
 					<input
						type="checkbox"
						checked={algoFormData.liquidity}
						onChange={(e) => handleAlgoData(e,10)}
 						className="form-checkbox h-4 w-4 text-blue-600 transition duration-150 ease-in-out rounded"
						style={{ borderRadius: "!25%" }}
 					/>
					<label className="select-lable ml-2 cursor-pointer" style={{ color: "black", fontSize: "14px" }}>Attempt to never take liquidity</label>
 				</div>
				)}
				{(algoFormData.algoType === "Vwap") && (
				<div className="flex w-full items-center">
 					<input
 						type="checkbox"
 						id="applyRules"
 						checked={algoFormData.speedUp}
 						onChange={(e) => handleAlgoData(e,18)}
 						className="form-checkbox h-4 w-4 text-blue-600 transition duration-150 ease-in-out rounded"
						style={{ borderRadius: "!25%" }}
 					/>
					<label className="select-lable ml-2 cursor-pointer" style={{ color: "black", fontSize: "14px" }}>Speed up when market approaches limit price</label>
 				</div>
				)}
				
				{/* Validation Errors Display */}
				{validationErrors.length > 0 && (
					<div className="w-full p-3 bg-red-50 border border-red-200 rounded-md">
						<ul className="text-red-700 text-sm space-y-1">
							{validationErrors.map((error, index) => (
								<li key={index} className="flex items-start">
									<span className="mr-2">•</span>
									{error}
								</li>
							))}
						</ul>
					</div>
				)}
				
				<Button
					className={"w-full mt-2 mx-0 modal-green-btn"}
					type="button"
					varient={"green"}
					CTA={"Save"}
					handleClick={handleSaveAlgo}
				/>
        	</div>
				
		</Modal>
	  </>
	);
  };
export default AlgoForm;